import { ActivityEventIcon } from '@/components/shared/ActivityEventIcon/ActivityEventIcon';
import { ClientWalletLink } from '@/components/shared/ClientWalletLink';
import { CollectionOfferTraits } from '@/components/shared/CollectionOfferTraits/CollectionOfferTraits';
import { UsdPrice } from '@/components/shared/UsdPrice/UsdPrice';
import { DATE_FORMAT, TIME_FORMAT } from '@/config/date';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { isAttributeOrder, isBurnActivity, isCollectionOrder } from '@/hooks/use-user-activity';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useENSResolver } from '@/hooks/useENSResolver';
import { getChainExplorerName, getChainExplorerUrl } from '@/utils/chainExplorers';
import { routes } from '@/utils/routes';
import { Box, Divider, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { formatAddress, formatTokenAmount } from '@onbeam/utils';
import { ArrowThickIcon, CollectionOfferIcon, DiagonalArrowIcon } from '@sphere/icons';
import { QuantityLabel, TableRowMediaItem } from '@sphere/ui';
import { format, formatDistance, parseISO } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { ActivityData, ActivityType, getActivityEventColor, getActivityEventTitle } from '../utils';

type Props = {
  hideItemColumn?: boolean;
} & ActivityData;

export const ActivityItemMobile = ({ hideItemColumn, ...activity }: Props) => {
  const marketplaceChain = useMarketplaceChain();
  const { t } = useTranslation('activity');
  const { userAddress } = useAuthentication();
  const { displayName: toDisplayName } = useENSResolver(activity.toAddress);
  const { displayName: fromDisplayName } = useENSResolver(activity.fromAddress);
  const chainId = marketplaceChain.id;
  const isCollectionOffer = isCollectionOrder(activity);
  const isBurn = isBurnActivity(activity);
  const date = new Date((activity.timestamp || 0) * 1000).toISOString();

  return (
    <Box bg="gray.2" p="space.16" rounded="radius.8" mb="space.8">
      <Flex justify="space-between" mb="space.16">
        {hideItemColumn ? (
          <Flex
            color={getActivityEventColor(activity.type as ActivityType)}
            align="center"
            gap="space.6"
          >
            {isCollectionOffer ? (
              <CollectionOfferIcon boxSize={rem(16)} />
            ) : (
              <ActivityEventIcon
                type={isBurn ? 'burn' : (activity.type as ActivityType)}
                boxSize={rem(16)}
              />
            )}
            <Text fontSize="xs">
              {getActivityEventTitle(
                (isCollectionOffer
                  ? `collection-${activity.type}`
                  : isBurn
                  ? 'burn'
                  : activity.type) as ActivityType,
                t,
              )}
            </Text>
            {!!activity.amount && activity.amount > 1 && (
              <QuantityLabel quantity={activity.amount} />
            )}
          </Flex>
        ) : isCollectionOffer ? (
          <TableRowMediaItem
            href={routes.collections.detail(
              activity.collection?.collectionId ?? '',
              marketplaceChain.routePrefix,
            )}
            title={formatAddress(activity.collection?.collectionName)}
            imageSrc={activity.collection?.collectionImage}
            subtitle={
              isAttributeOrder(activity) && (
                <CollectionOfferTraits
                  fontSize="xs"
                  // @ts-expect-error - Type is wrong
                  traits={[activity.order?.criteria?.data?.attribute]}
                />
              )
            }
            quantity={activity.amount}
            hideLinkOnSubtitle
          />
        ) : (
          <TableRowMediaItem
            href={
              activity.token
                ? routes.nft.detail(
                    activity.collection?.collectionId ?? '',
                    activity.token.tokenId ?? '',
                    marketplaceChain.routePrefix,
                  )
                : ''
            }
            title={activity.token?.tokenName || `#${activity.token?.tokenId}`}
            imageSrc={activity.token?.tokenImage || activity.collection?.collectionImage}
            subtitle={
              <Text fontSize="sm" color="gray.5" noOfLines={1} wordBreak="break-all">
                {formatAddress(activity.collection?.collectionName)}
              </Text>
            }
            quantity={activity.amount}
            {...(!activity.token?.tokenImage &&
              activity.collection?.collectionImage && {
                rounded: 'full',
                coverImage: true,
              })}
          />
        )}
        {chainId && activity.txHash && (
          <IconButton
            as="a"
            boxSize={rem(36)}
            minW="unset"
            ml="space.8"
            variant="secondary"
            href={getChainExplorerUrl(chainId, activity.txHash, 'tx')}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={getChainExplorerName(chainId)}
            title={getChainExplorerName(chainId)}
            icon={<DiagonalArrowIcon color="white" boxSize={rem(14)} />}
            flexShrink={0}
          />
        )}
      </Flex>
      <Flex fontSize="xs" align="center" justify="space-between">
        {!isCollectionOffer && (
          <Flex gap="space.8" align="center">
            {activity.fromAddress ? (
              <ClientWalletLink
                userAddress={userAddress}
                walletAddress={activity.fromAddress}
                username={fromDisplayName}
                href={routes.profile(activity.fromAddress, marketplaceChain.routePrefix)}
              />
            ) : (
              '--'
            )}
            <ArrowThickIcon color="gray.1" transform="rotate(180deg)" boxSize={rem(14)} />
            {activity.toAddress ? (
              <ClientWalletLink
                userAddress={userAddress}
                walletAddress={activity.toAddress}
                username={toDisplayName}
                href={routes.profile(activity.toAddress, marketplaceChain.routePrefix)}
              />
            ) : (
              '--'
            )}
          </Flex>
        )}
        <Text color="gray.1">
          <Tooltip
            label={format(parseISO(date), `${DATE_FORMAT}${TIME_FORMAT}`)}
            fontSize="md"
            gutter={16}
            placement="top"
            hasArrow
            arrowSize={8}
          >
            {`${formatDistance(parseISO(date), new Date(), {
              addSuffix: true,
            })}`}
          </Tooltip>
        </Text>
      </Flex>
      {(!hideItemColumn || !!activity.price?.amount?.decimal) && (
        <>
          <Divider my="space.10" />
          <Flex justify="space-between">
            {!hideItemColumn && (
              <Flex
                color={getActivityEventColor(activity.type as ActivityType)}
                align="center"
                gap="space.6"
              >
                {isCollectionOffer ? (
                  <CollectionOfferIcon boxSize={rem(16)} />
                ) : (
                  <ActivityEventIcon
                    type={isBurn ? 'burn' : (activity.type as ActivityType)}
                    boxSize={rem(16)}
                  />
                )}
                <Text fontSize="xs">
                  {getActivityEventTitle(
                    (isCollectionOffer
                      ? `collection-${activity.type}`
                      : isBurn
                      ? 'burn'
                      : activity.type) as ActivityType,
                    t,
                  )}
                </Text>
              </Flex>
            )}
            {!!activity.price?.amount?.decimal && (
              <Flex gap="space.4" fontSize="xs" lineHeight="3xl">
                <Tooltip
                  label={formatTokenAmount(activity.price.amount.decimal, {
                    maximumFractionDigits: 4,
                    symbol: activity.price.currency?.symbol,
                  })}
                  fontSize="md"
                  gutter={16}
                  placement="top"
                  hasArrow
                  arrowSize={8}
                >
                  <Text fontWeight="bold">
                    {formatTokenAmount(activity.price.amount.decimal, {
                      symbol: activity.price.currency?.symbol,
                    })}
                  </Text>
                </Tooltip>

                {!!activity.price?.amount?.usd && (
                  <UsdPrice
                    usdValue={activity.price?.amount?.usd}
                    isExact
                    color="gray.1"
                    prefix="("
                    suffix=")"
                  />
                )}
                {(activity.amount || 0) > 1 && t('table.labels.each')}
              </Flex>
            )}
          </Flex>
        </>
      )}
    </Box>
  );
};
